<!-- frontend/src/views/SecurityLockouts.vue -->
<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-3">
          <v-card-title class="d-flex align-center pa-6">
            <v-icon color="error" size="32" class="mr-3">mdi-shield-lock</v-icon>
            <span class="text-h5 font-weight-bold">Account Lockouts Management</span>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="refreshData" :loading="loading" icon>
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>

          <!-- Statistics Cards -->
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-card color="error" dark>
                  <v-card-text class="text-center">
                    <div class="text-h3 font-weight-bold">{{ stats.active_lockouts }}</div>
                    <div class="text-subtitle-1">Active Lockouts</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="warning" dark>
                  <v-card-text class="text-center">
                    <div class="text-h3 font-weight-bold">{{ stats.failed_attempts_24h }}</div>
                    <div class="text-subtitle-1">Failed Attempts (24h)</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="success" dark>
                  <v-card-text class="text-center">
                    <div class="text-h3 font-weight-bold">{{ stats.successful_logins_24h }}</div>
                    <div class="text-subtitle-1">Successful Logins (24h)</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="info" dark>
                  <v-card-text class="text-center">
                    <div class="text-h3 font-weight-bold">{{ stats.unique_ips_blocked }}</div>
                    <div class="text-subtitle-1">Unique IPs Blocked</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Search and Filters -->
            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search username or IP"
                  single-line
                  hide-details
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="filterType"
                  :items="lockoutTypes"
                  label="Filter by type"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
            </v-row>

            <!-- Lockouts Table -->
            <v-row class="mt-4">
              <v-col cols="12">
                <v-data-table
                  :headers="headers"
                  :items="filteredLockouts"
                  :loading="loading"
                  :search="search"
                  class="elevation-1"
                  :items-per-page="10"
                >
                  <!-- Username Column -->
                  <template v-slot:item.username="{ item }">
                    <v-chip
                      small
                      color="primary"
                      text-color="white"
                      class="font-weight-bold"
                    >
                      <v-icon left small>mdi-account</v-icon>
                      {{ item.username || 'N/A' }}
                    </v-chip>
                  </template>

                  <!-- IP Address Column -->
                  <template v-slot:item.ip_address="{ item }">
                    <code class="pa-1 grey lighten-3 rounded">{{ item.ip_address || 'N/A' }}</code>
                  </template>

                  <!-- Lockout Type Column -->
                  <template v-slot:item.lockout_type="{ item }">
                    <v-chip
                      small
                      :color="getLockoutTypeColor(item.lockout_type)"
                      text-color="white"
                    >
                      {{ item.lockout_type }}
                    </v-chip>
                  </template>

                  <!-- Time Remaining Column -->
                  <template v-slot:item.minutes_remaining="{ item }">
                    <v-chip
                      small
                      :color="getTimeRemainingColor(item.minutes_remaining)"
                      text-color="white"
                      class="font-weight-bold"
                    >
                      <v-icon left small>mdi-clock-outline</v-icon>
                      {{ item.minutes_remaining }} min
                    </v-chip>
                  </template>

                  <!-- Attempts Column -->
                  <template v-slot:item.attempts_count="{ item }">
                    <v-chip small color="error" text-color="white">
                      {{ item.attempts_count }} attempts
                    </v-chip>
                  </template>

                  <!-- Actions Column -->
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      color="success"
                      small
                      @click="openUnlockDialog(item)"
                      :loading="unlockingId === item.id"
                    >
                      <v-icon left small>mdi-lock-open</v-icon>
                      Unlock
                    </v-btn>
                    <v-btn
                      color="info"
                      small
                      icon
                      @click="viewDetails(item)"
                      class="ml-2"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Unlock Confirmation Dialog -->
    <v-dialog v-model="unlockDialog" max-width="600px">
      <v-card>
        <v-card-title class="text-h5 warning white--text">
          <v-icon color="white" class="mr-2">mdi-alert</v-icon>
          Confirm Account Unlock
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outlined dense class="mb-4">
            You are about to unlock this account. Please ensure you have verified the user's identity.
          </v-alert>

          <div v-if="selectedLockout">
            <v-simple-table>
              <tbody>
                <tr>
                  <td class="font-weight-bold">Username:</td>
                  <td>{{ selectedLockout.username }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">IP Address:</td>
                  <td>{{ selectedLockout.ip_address }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Failed Attempts:</td>
                  <td>{{ selectedLockout.attempts_count }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Time Remaining:</td>
                  <td>{{ selectedLockout.minutes_remaining }} minutes</td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-textarea
              v-model="unlockReason"
              label="Reason for unlock (optional)"
              placeholder="e.g., User verified via phone call"
              outlined
              rows="3"
              class="mt-4"
            ></v-textarea>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="unlockDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            @click="confirmUnlock"
            :loading="unlockingId !== null"
          >
            <v-icon left>mdi-lock-open</v-icon>
            Unlock Account
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="800px">
      <v-card v-if="selectedLockout">
        <v-card-title class="text-h5 primary white--text">
          <v-icon color="white" class="mr-2">mdi-information</v-icon>
          Lockout Details
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Username</v-list-item-subtitle>
                    <v-list-item-title class="font-weight-bold">{{ selectedLockout.username }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>IP Address</v-list-item-subtitle>
                    <v-list-item-title class="font-weight-bold">{{ selectedLockout.ip_address }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Lockout Type</v-list-item-subtitle>
                    <v-list-item-title class="font-weight-bold">{{ selectedLockout.lockout_type }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="6">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Failed Attempts</v-list-item-subtitle>
                    <v-list-item-title class="font-weight-bold error--text">{{ selectedLockout.attempts_count }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Locked At</v-list-item-subtitle>
                    <v-list-item-title class="font-weight-bold">{{ formatDateTime(selectedLockout.locked_at) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Auto-Unlock At</v-list-item-subtitle>
                    <v-list-item-title class="font-weight-bold">{{ formatDateTime(selectedLockout.locked_until) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="text-h6 mb-3">Recent Failed Attempts</div>
          <v-simple-table>
            <thead>
              <tr>
                <th>Time</th>
                <th>IP Address</th>
                <th>User Agent</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(attempt, index) in selectedLockout.recent_attempts" :key="index">
                <td>{{ formatDateTime(attempt.timestamp) }}</td>
                <td><code>{{ attempt.ip_address }}</code></td>
                <td class="text-truncate" style="max-width: 200px;">{{ attempt.user_agent }}</td>
                <td>{{ attempt.failure_reason || 'N/A' }}</td>
              </tr>
              <tr v-if="selectedLockout.recent_attempts.length === 0">
                <td colspan="4" class="text-center grey--text">No recent attempts recorded</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SecurityLockouts',
  data() {
    return {
      loading: false,
      lockouts: [],
      stats: {
        active_lockouts: 0,
        failed_attempts_24h: 0,
        successful_logins_24h: 0,
        total_lockouts_7d: 0,
        unique_ips_blocked: 0
      },
      search: '',
      filterType: 'All',
      lockoutTypes: ['All', 'USERNAME', 'IP', 'BOTH'],
      headers: [
        { text: 'Username', value: 'username' },
        { text: 'IP Address', value: 'ip_address' },
        { text: 'Type', value: 'lockout_type' },
        { text: 'Time Left', value: 'minutes_remaining' },
        { text: 'Attempts', value: 'attempts_count' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      unlockDialog: false,
      detailsDialog: false,
      selectedLockout: null,
      unlockReason: '',
      unlockingId: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      refreshInterval: null
    };
  },
  computed: {
    filteredLockouts() {
      if (this.filterType === 'All') {
        return this.lockouts;
      }
      return this.lockouts.filter(l => l.lockout_type === this.filterType);
    }
  },
  mounted() {
    this.fetchData();
    // Auto-refresh every 30 seconds
    this.refreshInterval = setInterval(() => {
      this.fetchData(true);
    }, 30000);
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async fetchData(silent = false) {
      if (!silent) this.loading = true;

      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Token ${token}` };

        const [lockedResponse, statsResponse] = await Promise.all([
          axios.get('/api/v1/manager/security/locked-accounts/', { headers }),
          axios.get('/api/v1/manager/security/stats/', { headers })
        ]);

        this.lockouts = lockedResponse.data.lockouts;
        this.stats = statsResponse.data;
      } catch (error) {
        this.showSnackbar('Error fetching data: ' + error.message, 'error');
      } finally {
        this.loading = false;
      }
    },
    refreshData() {
      this.fetchData();
    },
    openUnlockDialog(lockout) {
      this.selectedLockout = lockout;
      this.unlockReason = '';
      this.unlockDialog = true;
    },
    async confirmUnlock() {
      if (!this.selectedLockout) return;

      this.unlockingId = this.selectedLockout.id;

      try {
        const token = localStorage.getItem('token');
        await axios.post(
          '/api/v1/manager/security/unlock-account/',
          {
            lockout_id: this.selectedLockout.id,
            reason: this.unlockReason || 'Manual unlock by manager'
          },
          { headers: { Authorization: `Token ${token}` } }
        );

        this.showSnackbar('Account unlocked successfully', 'success');
        this.unlockDialog = false;
        this.fetchData();
      } catch (error) {
        this.showSnackbar('Error unlocking account: ' + error.message, 'error');
      } finally {
        this.unlockingId = null;
      }
    },
    viewDetails(lockout) {
      this.selectedLockout = lockout;
      this.detailsDialog = true;
    },
    getLockoutTypeColor(type) {
      const colors = {
        'USERNAME': 'primary',
        'IP': 'warning',
        'BOTH': 'error'
      };
      return colors[type] || 'grey';
    },
    getTimeRemainingColor(minutes) {
      if (minutes < 5) return 'success';
      if (minutes < 15) return 'warning';
      return 'error';
    },
    formatDateTime(datetime) {
      return new Date(datetime).toLocaleString();
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
